import { useState, useEffect } from 'react';
import { TranslationSettings, useTranslation } from '@/hooks/useTranslation';
import { useChatStore } from '@/stores/useChatStore';
import { toast } from 'react-hot-toast';

interface TranslationSettingsDialogProps {
  conversationId: string;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * 翻译设置对话框组件
 * 提供会话级别的翻译设置界面
 */
export default function TranslationSettingsDialog({
  conversationId,
  isOpen,
  onClose
}: TranslationSettingsDialogProps) {
  const {
    fetchSupportedLanguages,
    supportedLanguages,
    isLoadingLanguages,
  } = useTranslation();

  const getTranslationSettings = useChatStore((state: any) => state.getTranslationSettings);
  const updateTranslationSettings = useChatStore((state: any) => state.updateTranslationSettings);
  const resetTranslationSettings = useChatStore((state: any) => state.resetTranslationSettings);

  // 当前的翻译设置
  const [settings, setSettings] = useState<TranslationSettings>(() =>
    getTranslationSettings(conversationId)
  );

  // 加载语言列表
  useEffect(() => {
    if (isOpen) {
      fetchSupportedLanguages();
    }
  }, [isOpen, fetchSupportedLanguages]);

  // 当对话框打开时重新获取设置
  useEffect(() => {
    if (isOpen) {
      setSettings(getTranslationSettings(conversationId));
    }
  }, [isOpen, conversationId, getTranslationSettings]);

  // 保存设置
  const handleSave = () => {
    updateTranslationSettings(conversationId, settings);
    toast.success('翻译设置已保存');
    onClose();
  };

  // 重置设置
  const handleReset = () => {
    resetTranslationSettings(conversationId);
    setSettings(getTranslationSettings(conversationId));
    toast.success('已重置为默认设置');
  };

  // 获取语言显示名称
  const getLanguageDisplayName = (languageCode: string): string => {
    const language = supportedLanguages.find(lang => lang.code === languageCode);
    return language ? `${language.native_name}` : languageCode;
  };

  // 渲染语言选择器
  const renderLanguageSelector = (
    value: string,
    onChange: (value: string) => void,
    label: string
  ) => {

    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          disabled={isLoadingLanguages}
        >
          {/* 常用语言 */}
          {supportedLanguages.length > 0 && (
            <optgroup label="语言">
              {supportedLanguages.map((language) => (
                <option key={language.code} value={language.code}>
                  {language.native_name}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-[480px] max-h-[80vh] overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">翻译设置</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 内容区域 */}
        <div className="p-6 space-y-6 max-h-[calc(80vh-140px)] overflow-y-auto">
          {/* 自动翻译收到的消息 */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-lg text-gray-900">自动翻译 <strong>接收</strong> 的消息</h3>
              </div>
              <button
                onClick={() => setSettings(prev => ({ ...prev, autoTranslateIncoming: !prev.autoTranslateIncoming }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.autoTranslateIncoming ? 'bg-blue-600' : 'bg-gray-200'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.autoTranslateIncoming ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </div>

            {/* 收到消息的目标语言 */}
            <div className="ml-4">
              {renderLanguageSelector(
                settings.incomingTargetLanguage,
                (value) => setSettings(prev => ({ ...prev, incomingTargetLanguage: value })),
                "接收的消息翻译为"
              )}
              <div className="mt-2 text-right">
                <span className="text-sm text-gray-400">
                  {getLanguageDisplayName(settings.incomingTargetLanguage)}
                </span>
              </div>
            </div>
          </div>

          {/* 自动翻译发送的消息 */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-lg text-gray-900">自动翻译 <strong>发送</strong> 的消息</h3>
              </div>
              <button
                onClick={() => setSettings(prev => ({ ...prev, autoTranslateOutgoing: !prev.autoTranslateOutgoing }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.autoTranslateOutgoing ? 'bg-blue-600' : 'bg-gray-200'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.autoTranslateOutgoing ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </div>

            {/* 发送消息的目标语言 */}
            <div className="ml-4">
              {renderLanguageSelector(
                settings.outgoingTargetLanguage,
                (value) => setSettings(prev => ({ ...prev, outgoingTargetLanguage: value })),
                "发出的消息翻译为"
              )}
              <div className="mt-2 text-right">
                <span className="text-sm text-gray-400">
                  {getLanguageDisplayName(settings.outgoingTargetLanguage)}
                </span>
              </div>
            </div>
          </div>

        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
          >
            恢复默认
          </button>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              取消
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              保存设置
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
